/**
 * Feature Gating Utility
 * Checks if a user's company has access to specific features based on plan tier or custom features
 */

const FEATURE_TIERS = {
  // Starter features (always available)
  basic_scheduling: 'starter',
  time_tracking: 'starter',
  
  // Small Business features
  project_management: 'small_business',
  project_communication: 'small_business',
  upload_pictures: 'small_business',
  gps_tracking: 'small_business',
  
  // Mid Size features
  material_ordering: 'mid_size',
  stock_management: 'mid_size',
  expense_reimbursement: 'mid_size',
  
  // Big Business features
  sub_projects: 'big_business',
  material_returns: 'big_business',
  material_transfers: 'big_business',
  upload_videos: 'big_business',
  subcontractors: 'big_business',
  upload_invoices: 'big_business',
  equipment_management: 'big_business',
  payroll_summary: 'big_business',
  excel_export: 'big_business',
  invoice_ocr: 'big_business',
  
  // Mid Size features (PWA)
  pwa_install: 'mid_size',
  push_notifications: 'mid_size'
};

const PLAN_HIERARCHY = ['starter', 'small_business', 'mid_size', 'big_business', 'enterprise'];

/**
 * Check if user has access to a feature
 * @param {Object} user - User object with company_plan and company_custom_features
 * @param {string} featureName - Feature key to check
 * @returns {boolean} - True if user has access to the feature
 */
export const hasFeature = (user, featureName) => {
  if (!user) return false;
  
  // Super admin has access to everything
  if (user.role === 'super_admin') return true;
  
  const userPlan = user.company_plan || 'starter';
  const customFeatures = user.company_custom_features || [];
  
  // Check if feature is in custom features (negotiated add-ons)
  if (customFeatures.includes(featureName)) {
    return true;
  }
  
  // Enterprise plan has access to everything
  if (userPlan === 'enterprise') {
    return true;
  }
  
  // Check plan tier
  const requiredTier = FEATURE_TIERS[featureName];
  
  // If feature not in tiers, assume it's always available
  if (!requiredTier) {
    return true;
  }
  
  const userTierIndex = PLAN_HIERARCHY.indexOf(userPlan);
  const requiredTierIndex = PLAN_HIERARCHY.indexOf(requiredTier);
  
  // User has access if their plan tier is equal to or higher than required tier
  return userTierIndex >= requiredTierIndex;
};

/**
 * Get the required plan name for a feature
 * @param {string} featureName - Feature key
 * @returns {string} - Plan name required for the feature
 */
export const getRequiredPlan = (featureName) => {
  const tier = FEATURE_TIERS[featureName] || 'starter';
  const planNames = {
    starter: 'Starter',
    small_business: 'Small Business',
    mid_size: 'Mid Size Business',
    big_business: 'Big Business',
    enterprise: 'Enterprise'
  };
  return planNames[tier] || 'Starter';
};

/**
 * Get user's current plan display name
 * @param {Object} user - User object
 * @returns {string} - Display name of current plan
 */
export const getCurrentPlanName = (user) => {
  const plan = user?.company_plan || 'starter';
  const planNames = {
    starter: 'Starter',
    small_business: 'Small Business',
    mid_size: 'Mid Size Business',
    big_business: 'Big Business',
    enterprise: 'Enterprise',
    trial: 'Trial'
  };
  return planNames[plan] || 'Starter';
};
